import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from '@/lib/api';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { AlertTriangle, Filter, ArrowUp, ArrowDown, ArrowUpDown, ClipboardList, FileText, Clock, Users as UsersIcon } from 'lucide-react';
import logger from '@/lib/logger';
import { AccessLogsModal, AuditLogsModal } from '@/components/UserAuditModals';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import TimeClockTab from '@/components/TimeClockTab';

function UserManagement({ user }) {
  const isAdmin = (user?.role || '').toUpperCase() === 'ADMIN';
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [deleteUserOpen, setDeleteUserOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'full_name', direction: 'asc' });
  const [accessLogsOpen, setAccessLogsOpen] = useState(false);
  const [auditLogsOpen, setAuditLogsOpen] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    password: '',
    role: 'CLIENT',
    linked_client_id: '',
  });
  const [newPassword, setNewPassword] = useState('');

  // Filter + sort users
  const filteredUsers = useMemo(() => {
    let filtered = users;
    
    if (roleFilter !== 'all') {
      filtered = filtered.filter(u => u.role === roleFilter);
    }
    
    if (statusFilter !== 'all') {
      if (statusFilter === 'active') {
        filtered = filtered.filter(u => u.active && !u.blocked);
      } else if (statusFilter === 'inactive') {
        filtered = filtered.filter(u => !u.active && !u.blocked);
      } else if (statusFilter === 'blocked') {
        filtered = filtered.filter(u => u.blocked);
      }
    }

    // Sort
    if (sortConfig.key) {
      filtered = [...filtered].sort((a, b) => {
        let av = a[sortConfig.key];
        let bv = b[sortConfig.key];
        // Special case: status — compute composite for ordering active > inactive > blocked
        if (sortConfig.key === 'status') {
          const score = (u) => (u.blocked ? 2 : (u.active ? 0 : 1));
          av = score(a); bv = score(b);
        }
        // Strings: lowercased compare
        if (typeof av === 'string') av = av.toLowerCase();
        if (typeof bv === 'string') bv = bv.toLowerCase();
        if (av == null) return 1;
        if (bv == null) return -1;
        if (av < bv) return sortConfig.direction === 'asc' ? -1 : 1;
        if (av > bv) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return filtered;
  }, [users, roleFilter, statusFilter, sortConfig]);

  const requestSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) return <ArrowUpDown className="w-3.5 h-3.5 inline-block ml-1 text-gray-400" />;
    return sortConfig.direction === 'asc'
      ? <ArrowUp className="w-3.5 h-3.5 inline-block ml-1 text-emerald-700" />
      : <ArrowDown className="w-3.5 h-3.5 inline-block ml-1 text-emerald-700" />;
  };

  const fetchUsers = useCallback(async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      logger.error('Error fetching users:', error);
      toast.error('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
    } else {
      setLoading(false);
    }
  }, [fetchUsers, isAdmin]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/users', formData);
      toast.success('Usuário criado com sucesso!');
      setOpen(false);
      setFormData({ email: '', full_name: '', password: '', role: 'CLIENT', linked_client_id: '' });
      fetchUsers();
    } catch (error) {
      logger.error('Error creating user:', error);
      toast.error(error.response?.data?.detail || 'Erro ao criar usuário');
    }
  };

  const handleUpdateStatus = async (userId, active) => {
    try {
      await api.put(`/users/${userId}/status?active=${active}`);
      toast.success('Status atualizado com sucesso!');
      fetchUsers();
    } catch (error) {
      logger.error('Error updating status:', error);
      toast.error('Erro ao atualizar status');
    }
  };

  const handleBlockUser = async (userId, blocked) => {
    try {
      await api.put(`/users/${userId}/block?blocked=${blocked}`);
      toast.success(blocked ? 'Usuário bloqueado!' : 'Usuário desbloqueado!');
      fetchUsers();
    } catch (error) {
      logger.error('Error blocking user:', error);
      toast.error(error.response?.data?.detail || 'Erro ao bloquear usuário');
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    
    try {
      await api.delete(`/users/${selectedUser.user_id}`);
      toast.success('Usuário excluído com sucesso!');
      setDeleteUserOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      logger.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || 'Erro ao excluir usuário');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!selectedUser || !newPassword) return;
    
    try {
      await api.put(`/users/${selectedUser.user_id}/reset-password`, {
        new_password: newPassword
      });
      toast.success('Senha redefinida com sucesso!');
      setResetPasswordOpen(false);
      setSelectedUser(null);
      setNewPassword('');
    } catch (error) {
      logger.error('Error resetting password:', error);
      toast.error(error.response?.data?.detail || 'Erro ao redefinir senha');
    }
  };

  const openResetPasswordDialog = (userToReset) => {
    setSelectedUser(userToReset);
    setNewPassword('');
    setResetPasswordOpen(true);
  };

  const openDeleteDialog = (userToDelete) => {
    setSelectedUser(userToDelete);
    setDeleteUserOpen(true);
  };

  const getRoleBadge = (role) => {
    const colors = {
      ADMIN: 'bg-purple-100 text-purple-700',
      CONSULTANT: 'bg-emerald-100 text-emerald-700',
      CLIENT: 'bg-blue-100 text-blue-700',
    };
    const labels = {
      ADMIN: 'Administrador',
      CONSULTANT: 'Consultor',
      CLIENT: 'Cliente',
    };
    return (
      <Badge className={colors[role] || 'bg-gray-200 text-gray-700'}>
        {labels[role] || role}
      </Badge>
    );
  };

  const getStatusBadge = (u) => {
    if (u.blocked) {
      return <Badge className="bg-red-100 text-red-700">Bloqueado</Badge>;
    }
    return u.active ? (
      <Badge className="bg-green-100 text-green-700">Ativo</Badge>
    ) : (
      <Badge className="bg-gray-200 text-gray-700">Inativo</Badge>
    );
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-800"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-semibold text-gray-900">Gerenciamento de Usuários</h1>
          <p className="text-gray-600 mt-1">
            {isAdmin
              ? 'Crie e gerencie acesso de usuários ao sistema, além do controle de ponto dos consultores.'
              : 'Registre seu ponto e consulte seu histórico.'}
          </p>
        </div>

        <Tabs defaultValue={isAdmin ? 'users' : 'time-clock'} className="w-full">
          <TabsList data-testid="user-mgmt-tabs">
            {isAdmin && (
              <TabsTrigger value="users" data-testid="tab-users">
                <UsersIcon className="w-4 h-4 mr-2" /> Lista de Usuários
              </TabsTrigger>
            )}
            <TabsTrigger value="time-clock" data-testid="tab-time-clock">
              <Clock className="w-4 h-4 mr-2" /> Controle de Ponto
            </TabsTrigger>
          </TabsList>

          {isAdmin && (
          <TabsContent value="users" className="mt-4 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex-1" />
          <div className="flex items-center gap-3">
            {/* Role Filter */}
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-48" data-testid="user-role-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filtrar por função" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as Funções</SelectItem>
                <SelectItem value="ADMIN">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-purple-500" />
                    Administrador
                  </div>
                </SelectItem>
                <SelectItem value="CONSULTANT">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-emerald-500" />
                    Consultor
                  </div>
                </SelectItem>
                <SelectItem value="CLIENT">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-blue-500" />
                    Cliente
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>

            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40" data-testid="user-status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos Status</SelectItem>
                <SelectItem value="active">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-500" />
                    Ativos
                  </div>
                </SelectItem>
                <SelectItem value="inactive">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-gray-400" />
                    Inativos
                  </div>
                </SelectItem>
                <SelectItem value="blocked">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-red-500" />
                    Bloqueados
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>

            {user?.role?.toUpperCase() === 'ADMIN' && (
              <>
                <Button
                  variant="outline"
                  onClick={() => setAccessLogsOpen(true)}
                  data-testid="open-access-logs-button"
                  title="Histórico de logins"
                >
                  <ClipboardList className="w-4 h-4 mr-2" />
                  Logs de Acesso
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setAuditLogsOpen(true)}
                  data-testid="open-audit-logs-button"
                  title="Histórico de edições"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Logs de Edição
                </Button>
              </>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="create-user-button">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Novo Usuário
                </Button>
              </DialogTrigger>
              <DialogContent>
              <DialogHeader>
                <DialogTitle>Criar Novo Usuário</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="full_name">Nome Completo</Label>
                  <Input
                    id="full_name"
                    type="text"
                    data-testid="user-fullname-input"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    placeholder="Nome do usuário"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="user-email-input"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="email@exemplo.com"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="password">Senha Inicial</Label>
                  <Input
                    id="password"
                    type="password"
                    data-testid="user-password-input"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Senha de acesso"
                    required
                    minLength={6}
                  />
                  <p className="text-xs text-gray-500 mt-1">Mínimo de 6 caracteres. Compartilhe esta senha com o usuário.</p>
                </div>
                <div>
                  <Label htmlFor="role">Função</Label>
                  <Select
                    value={formData.role}
                    onValueChange={(value) => setFormData({ ...formData, role: value })}
                  >
                    <SelectTrigger data-testid="user-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ADMIN">Administrador</SelectItem>
                      <SelectItem value="CONSULTANT">Consultor</SelectItem>
                      <SelectItem value="CLIENT">Cliente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-emerald-800 hover:bg-emerald-900" data-testid="submit-user-button">
                    Criar Usuário
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        <Card className="p-6 border border-gray-200">
          {/* Filter summary */}
          {(roleFilter !== 'all' || statusFilter !== 'all') && (
            <div className="flex items-center gap-2 mb-4 pb-4 border-b border-gray-100">
              <span className="text-sm text-gray-500">Filtros ativos:</span>
              {roleFilter !== 'all' && (
                <Badge className="bg-gray-100 text-gray-700">
                  {roleFilter === 'ADMIN' ? 'Administrador' : roleFilter === 'CONSULTANT' ? 'Consultor' : 'Cliente'}
                </Badge>
              )}
              {statusFilter !== 'all' && (
                <Badge className="bg-gray-100 text-gray-700">
                  {statusFilter === 'active' ? 'Ativos' : statusFilter === 'inactive' ? 'Inativos' : 'Bloqueados'}
                </Badge>
              )}
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-gray-500 hover:text-gray-700 ml-2"
                onClick={() => { setRoleFilter('all'); setStatusFilter('all'); }}
              >
                Limpar filtros
              </Button>
              <span className="text-sm text-gray-400 ml-auto">
                {filteredUsers.length} de {users.length} usuários
              </span>
            </div>
          )}
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th
                    className="text-left py-3 px-4 font-semibold text-gray-900 cursor-pointer select-none hover:bg-gray-50"
                    onClick={() => requestSort('full_name')}
                    data-testid="sort-name"
                  >Nome<SortIcon column="full_name" /></th>
                  <th
                    className="text-left py-3 px-4 font-semibold text-gray-900 cursor-pointer select-none hover:bg-gray-50"
                    onClick={() => requestSort('email')}
                    data-testid="sort-email"
                  >Email<SortIcon column="email" /></th>
                  <th
                    className="text-left py-3 px-4 font-semibold text-gray-900 cursor-pointer select-none hover:bg-gray-50"
                    onClick={() => requestSort('role')}
                    data-testid="sort-role"
                  >Função<SortIcon column="role" /></th>
                  <th
                    className="text-left py-3 px-4 font-semibold text-gray-900 cursor-pointer select-none hover:bg-gray-50"
                    onClick={() => requestSort('status')}
                    data-testid="sort-status"
                  >Status<SortIcon column="status" /></th>
                  <th
                    className="text-left py-3 px-4 font-semibold text-gray-900 cursor-pointer select-none hover:bg-gray-50"
                    onClick={() => requestSort('created_at')}
                    data-testid="sort-created"
                  >Criado em<SortIcon column="created_at" /></th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-gray-500">
                      Nenhum usuário encontrado com os filtros selecionados
                    </td>
                  </tr>
                ) : filteredUsers.map((u) => (
                  <tr key={u.user_id} className="border-b border-gray-100" data-testid={`user-row-${u.user_id}`}>
                    <td className="py-4 px-4">
                      <p className="font-medium text-gray-900">{u.full_name}</p>
                    </td>
                    <td className="py-4 px-4">
                      <p className="text-gray-600">{u.email}</p>
                    </td>
                    <td className="py-4 px-4">{getRoleBadge(u.role)}</td>
                    <td className="py-4 px-4">{getStatusBadge(u)}</td>
                    <td className="py-4 px-4 text-sm text-gray-600">
                      {new Date(u.created_at).toLocaleDateString('pt-BR')}
                    </td>
                    <td className="py-4 px-4">
                      {u.user_id !== user.user_id && (
                        <div className="flex flex-wrap gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openResetPasswordDialog(u)}
                            data-testid={`reset-password-${u.user_id}`}
                          >
                            Redefinir Senha
                          </Button>
                          {u.active ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleUpdateStatus(u.user_id, false)}
                              className="text-amber-600 border-amber-200 hover:bg-amber-50"
                              data-testid={`deactivate-${u.user_id}`}
                            >
                              Desativar
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              onClick={() => handleUpdateStatus(u.user_id, true)}
                              className="bg-green-600 hover:bg-green-700 text-white"
                              data-testid={`activate-${u.user_id}`}
                            >
                              Ativar
                            </Button>
                          )}
                          {u.blocked ? (
                            <Button
                              size="sm"
                              onClick={() => handleBlockUser(u.user_id, false)}
                              className="bg-blue-600 hover:bg-blue-700 text-white"
                              data-testid={`unblock-${u.user_id}`}
                            >
                              Desbloquear
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleBlockUser(u.user_id, true)}
                              className="text-orange-600 border-orange-200 hover:bg-orange-50"
                              data-testid={`block-${u.user_id}`}
                            >
                              Bloquear
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openDeleteDialog(u)}
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            data-testid={`delete-${u.user_id}`}
                          >
                            Excluir
                          </Button>
                        </div>
                      )}
                      {u.user_id === user.user_id && (
                        <span className="text-sm text-gray-500 italic">Você</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Dialog para redefinir senha */}
        <Dialog open={resetPasswordOpen} onOpenChange={setResetPasswordOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Redefinir Senha</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-4">
                  Redefinindo senha para: <strong>{selectedUser?.full_name}</strong> ({selectedUser?.email})
                </p>
                <Label htmlFor="new_password">Nova Senha</Label>
                <Input
                  id="new_password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Nova senha"
                  required
                  minLength={8}
                  data-testid="new-password-input"
                />
                <p className="text-xs text-gray-500 mt-1">Mínimo de 8 caracteres. Compartilhe esta senha com o usuário.</p>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setResetPasswordOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-emerald-800 hover:bg-emerald-900">
                  Redefinir Senha
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Dialog para excluir usuário */}
        <Dialog open={deleteUserOpen} onOpenChange={setDeleteUserOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center text-red-600">
                <AlertTriangle className="w-5 h-5 mr-2" />
                Excluir Usuário
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-800 text-sm">
                  <strong>Atenção!</strong> Esta ação é irreversível. O usuário será permanentemente excluído do sistema.
                </p>
              </div>
              <p className="text-gray-600">
                Tem certeza que deseja excluir o usuário <strong>{selectedUser?.full_name}</strong> ({selectedUser?.email})?
              </p>
              <div className="flex justify-end space-x-3 pt-4">
                <Button type="button" variant="outline" onClick={() => {
                  setDeleteUserOpen(false);
                  setSelectedUser(null);
                }}>
                  Cancelar
                </Button>
                <Button
                  type="button"
                  onClick={handleDeleteUser}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  Excluir Usuário
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        {/* Audit Reports Modals */}
        <AccessLogsModal open={accessLogsOpen} onOpenChange={setAccessLogsOpen} users={users} />
        <AuditLogsModal open={auditLogsOpen} onOpenChange={setAuditLogsOpen} users={users} />
          </TabsContent>
          )}

          <TabsContent value="time-clock" className="mt-4">
            <TimeClockTab user={user} />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}

export default UserManagement;
